// localStorage cache for AI-generated suggestions

const KEY_PREFIX = 'scite:suggest:'

// Bound growth: keep only the most-recently-written entries per namespace.
const MAX_ENTRIES_PER_NAMESPACE = 20

export const SUGGESTION_REVALIDATE_VISITS = 4
export const SUGGESTION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

const isBrowser = () => typeof window !== 'undefined' && !!window.localStorage

const storageKey = namespace => `${KEY_PREFIX}${namespace}`

const readNamespace = namespace => {
  if (!isBrowser()) return {}
  try {
    const raw = window.localStorage.getItem(storageKey(namespace))
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (e) {
    return {}
  }
}

const writeNamespace = (namespace, map) => {
  if (!isBrowser()) return
  try {
    const pruned = pruneEntries(map)
    window.localStorage.setItem(storageKey(namespace), JSON.stringify(pruned))
  } catch (e) {
    // Ignore quota / serialization / private-mode failures.
  }
}

// Keep the MAX_ENTRIES_PER_NAMESPACE newest entries (by ts).
const pruneEntries = map => {
  const keys = Object.keys(map)
  if (keys.length <= MAX_ENTRIES_PER_NAMESPACE) return map
  const kept = keys.sort((a, b) => (map[b]?.ts || 0) - (map[a]?.ts || 0)).slice(0, MAX_ENTRIES_PER_NAMESPACE)
  const next = {}
  for (const k of kept) next[k] = map[k]
  return next
}

/**
 * Read a cached suggestion entry.
 *
 * @param {string} namespace
 * @param {string} key
 * @returns {{ payload: *, ts: number, visits: number } | null}
 */
export const readSuggestionCache = (namespace, key) => {
  if (!key) return null
  const entry = readNamespace(namespace)[key]
  return entry && 'payload' in entry ? entry : null
}

/**
 * Write a freshly generated payload, resetting the visit count and timestamp.
 *
 * @param {string} namespace
 * @param {string} key
 * @param {*} payload
 */
export const writeSuggestionCache = (namespace, key, payload) => {
  if (!key) return
  const map = readNamespace(namespace)
  map[key] = { payload, ts: Date.now(), visits: 0 }
  writeNamespace(namespace, map)
}

/**
 * Increment the visit count for an entry (used to trigger revalidation after
 * N reads). No-op when the entry is absent.
 *
 * @param {string} namespace
 * @param {string} key
 * @returns {{ payload: *, ts: number, visits: number } | null} the updated entry
 */
export const recordVisit = (namespace, key) => {
  if (!key) return null
  const map = readNamespace(namespace)
  const entry = map[key]
  if (!entry) return null
  entry.visits = (entry.visits || 0) + 1
  map[key] = entry
  writeNamespace(namespace, map)
  return entry
}

/**
 * Whether a cached entry should be regenerated in the background.
 *
 * @param {{ ts: number, visits: number } | null} entry
 * @param {{ maxVisits?: number, maxAgeMs?: number }} [opts]
 * @returns {boolean}
 */
export const isStale = (entry, { maxVisits = SUGGESTION_REVALIDATE_VISITS, maxAgeMs = SUGGESTION_MAX_AGE_MS } = {}) => {
  if (!entry) return true
  if ((entry.visits || 0) >= maxVisits) return true
  if (Date.now() - (entry.ts || 0) > maxAgeMs) return true
  return false
}
